import { useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { Head } from "components/Head"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useDining } from "hooks/dining/useDining"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import _, { take } from "lodash"
import { CustomIcon, DiningIcon } from "components/icons"
import { getFeatureDetail } from "utils/feature"
import { EmptyData } from "components/EmptyData"
import { getIcon } from "utils/icon"
import { VenueSelector } from "components/VenueSelector"
import config from "config"
import { useSection } from "hooks/section"
import { OccupantImageCard } from "extensions/em-core/components/ImageCard"
import { useNavigate } from "react-router-dom"

const DEFAULT_FILTER_ICON = <DiningIcon />
const SECTION_DISPLAY_LIMIT = 4

export const DiningPage = () => {
  const { occupants, isMultiOrdinalVenue } = useVenue()
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    changeSubCategory,
    changeVenueFilter,
  } = useDining(occupants)
  const { filteredSectionsSorted } = useSection({
    category: "eatingdrinking",
  })
  const { t } = useTranslation({ defaultValue: null })
  const navigate = useNavigate()

  const filterItems = useMemo(
    () => [
      { value: "ALL", label: t("All"), icon: DEFAULT_FILTER_ICON },
      ...categories.map((category) => ({
        value: category.id,
        label: category.properties.name,
        icon: category.properties?.icon_path ? (
          <CustomIcon path={category.properties?.icon_path} />
        ) : (
          DEFAULT_FILTER_ICON
        ),
      })),
    ],
    [categories, t]
  )

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  //TODO:: refactor to new layout for kiosk-v later
  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const showFilterBar = isKioskMode || filterItems.length > 1
  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const {
    root,
    filterSection,
    filterContainer,
    filterButton,
    gridContainer,
    emptyDataContainer,
    venueSelector,
  } = styleOverrides

  const handleChangeSubCategory = useCallback(
    (_, id) => {
      changeSubCategory(id)
    },
    [changeSubCategory]
  )

  return (
    <>
      <Head title={t("Dining")} />
      <Box sx={root}>
        {showFilterBar && (
          <Box sx={filterSection}>
            <HorizontalScrollPane
              className="subCategory-filterbar"
              stackProps={{
                sx: filterContainer,
              }}
            >
              <CategoryFilterBar
                sx={filterButton}
                activeCategory={currentCategory}
                categories={categories}
                onChange={handleChangeSubCategory}
                renderFilterButton={CategoryFilterChip}
              />
            </HorizontalScrollPane>
          </Box>
        )}
        {restaurants.length > 0 || filteredSectionsSorted?.length > 0 ? (
          <Box sx={gridContainer}>
            {take(filteredSectionsSorted, SECTION_DISPLAY_LIMIT)
              .concat(restaurants)
              .map((restaurant) => {
                const {
                  id,
                  name,
                  featuredImage,
                  logo,
                  categoryIconPath,
                  level,
                  isFeatured,
                } = getFeatureDetail(restaurant) || {}

                const isSection = restaurant?.feature_type === "section"

                const restaurantName = t(name)

                const levelText = isMultiOrdinalVenue
                  ? t("Level Text", { level: t(level) })
                  : ""

                const subcategory = _.first(
                  restaurant?.properties?.local_sub_categories
                )
                const subcategoryName = _.get(subcategory, "properties.name")

                return (
                  <OccupantImageCard
                    key={id}
                    id={id}
                    coverImage={featuredImage}
                    logoSrc={logo}
                    iconPath={categoryIconPath}
                    primaryText={restaurantName}
                    secondaryText={t(subcategoryName)}
                    adornmentText={levelText}
                    isFeatured={isFeatured}
                    isSection={isSection}
                    onClick={() => navigate(`/maps/place/${id}`)}
                  />
                )
              })}
          </Box>
        ) : (
          <Box sx={emptyDataContainer}>
            <EmptyData
              icon={emptyIcon}
              primary={t("Coming soon")}
              secondary={t("Stay tuned for the update")}
            />
          </Box>
        )}
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={venueFilter}
              venues={venues}
              onClickVenue={changeVenueFilter}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </Box>
    </>
  )
}
